import { useNavigate } from '@tanstack/react-router';
import { App } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateOperatorStatus } from '@/apis/operator.api.ts';
import { useSignOutMutation } from '@/apis/user.api.ts';
import { Button } from '@/elements';
import { setOperatorStatus } from '@/routes/arc/stores/operator.store.ts';
import { checkSipHardwareAndReturn } from '@/utils/check-sip-hardware';

export const Route = createFileRoute({
  component: OfflinePage,
});

function OfflinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const updateOperatorStatusMutation = useUpdateOperatorStatus();
  const signOutMutation = useSignOutMutation();
  const [loadingType, setLoadingType] = useState<'ADMIN' | 'AVAILABLE'>(
    'AVAILABLE',
  );

  return (
    <div className={'flex min-h-0 flex-1 flex-col items-center justify-center'}>
      <Icon />
      <div className={'my-4 font-semibold'}>
        {t('arc.widgets.offline.OfflinePage.currentlyOffline')}
      </div>
      <div className={'flex w-80 flex-col gap-4'}>
        <Button
          size={'large'}
          color={'primary'}
          variant={'solid'}
          loading={
            updateOperatorStatusMutation.isPending &&
            loadingType === 'AVAILABLE'
          }
          onClick={async () => {
            const isHardwareReady = await checkSipHardwareAndReturn();
            if (!isHardwareReady) {
              return;
            }
            setLoadingType('AVAILABLE');
            updateOperatorStatusMutation
              .mutateAsync({
                status: 'AVAILABLE',
                description: 'Manual Update',
              })
              .then(() => {
                setOperatorStatus('AVAILABLE');
                navigate({
                  to: '/arc/call/available',
                  replace: true,
                }).then();
              })
              .catch(() => {
                message.error(
                  t('arc.widgets.offline.OfflinePage.messages.failedToUpdate'),
                );
              });
          }}
        >
          {t('arc.widgets.offline.OfflinePage.available')}
        </Button>
        <Button
          size={'large'}
          color={'primary'}
          variant={'outlined'}
          loading={
            updateOperatorStatusMutation.isPending && loadingType === 'ADMIN'
          }
          onClick={() => {
            setLoadingType('ADMIN');
            updateOperatorStatusMutation
              .mutateAsync({
                status: 'ADMIN',
                description: 'Manual Update',
              })
              .then(() => {
                setOperatorStatus('ADMIN');
                navigate({ to: '/arc/admin', replace: true }).then();
              })
              .catch(() => {
                message.error(
                  t('arc.widgets.offline.OfflinePage.messages.failedToUpdate'),
                );
              });
          }}
        >
          {t('arc.widgets.offline.OfflinePage.admin')}
        </Button>
        <Button
          onClick={() => {
            signOutMutation.mutateAsync().catch(() => {
              message.error(
                t('arc.widgets.offline.OfflinePage.messages.failedToUpdate'),
              );
            });
          }}
          color={'primary'}
          variant={'link'}
          size={'large'}
          loading={signOutMutation.isPending}
        >
          {t('arc.widgets.offline.OfflinePage.logout')}
        </Button>
      </div>
    </div>
  );
}

function Icon() {
  return (
    <svg
      width="50"
      height="50"
      viewBox="0 0 50 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 12.5C10 9.18479 11.317 6.00537 13.6612 3.66117C16.0054 1.31696 19.1848 0 22.5 0C25.8152 0 28.9946 1.31696 31.3388 3.66117C33.683 6.00537 35 9.18479 35 12.5C35 15.8152 33.683 18.9946 31.3388 21.3388C28.9946 23.683 25.8152 25 22.5 25C19.1848 25 16.0054 23.683 13.6612 21.3388C11.317 18.9946 10 15.8152 10 12.5ZM7.055 31.68C11.0625 29.235 16.5125 27.5 22.5 27.5C23.6175 27.5 24.7175 27.56 25.79 27.675C26.2194 27.7211 26.6296 27.8776 26.9805 28.1293C27.3315 28.3811 27.6112 28.7194 27.7925 29.1114C27.9738 29.5034 28.0504 29.9357 28.0149 30.3661C27.9794 30.7966 27.833 31.2105 27.59 31.5675C25.8965 34.053 24.9937 36.9924 25 40C25 42.3 25.5175 44.475 26.4375 46.4175C26.6168 46.7959 26.6981 47.2133 26.674 47.6314C26.65 48.0494 26.5213 48.4547 26.2998 48.8101C26.0783 49.1654 25.7712 49.4595 25.4064 49.6652C25.0417 49.8709 24.6312 49.9818 24.2125 49.9875C23.6475 49.995 23.075 50 22.5 50C16.9275 50 11.6625 49.65 7.7175 48.605C5.755 48.085 3.9075 47.34 2.5075 46.215C1.025 45.025 0 43.3625 0 41.25C0 39.2825 0.895 37.4425 2.11 35.9025C3.345 34.34 5.0525 32.9025 7.055 31.6775V31.68ZM35.17 38.705C34.9425 39.553 34.9422 40.4459 35.1693 41.2941C35.3964 42.1422 35.8428 42.9155 36.4636 43.5364C37.0845 44.1572 37.8578 44.6036 38.7059 44.8307C39.5541 45.0578 40.447 45.0575 41.295 44.83L35.17 38.705ZM38.705 35.17L44.83 41.295C45.0575 40.447 45.0578 39.5541 44.8307 38.7059C44.6036 37.8578 44.1572 37.0845 43.5364 36.4636C42.9155 35.8428 42.1422 35.3964 41.2941 35.1693C40.4459 34.9422 39.553 34.9425 38.705 35.17ZM30 40C30 37.3478 31.0536 34.8043 32.9289 32.9289C34.8043 31.0536 37.3478 30 40 30C42.6522 30 45.1957 31.0536 47.0711 32.9289C48.9464 34.8043 50 37.3478 50 40C50 42.6522 48.9464 45.1957 47.0711 47.0711C45.1957 48.9464 42.6522 50 40 50C37.3478 50 34.8043 48.9464 32.9289 47.0711C31.0536 45.1957 30 42.6522 30 40Z"
        fill="black"
        fillOpacity="0.15"
      />
    </svg>
  );
}
